/**
 * Musical note and key signature type definitions
 *
 * This module defines structured types for representing musical notes and key signatures,
 * eliminating ambiguity from string-based representations.
 */
// ============================================================================
// Type Guards
// ============================================================================
/**
 * Type guard to check if a value is a valid Note object.
 *
 * Validates that:
 * - Value is a non-null object
 * - Has a valid noteName (C-B)
 * - Has a valid modifier (n, s, f)
 *
 * @param value - Value to check
 * @returns true if value is a valid Note, false otherwise
 *
 * @example
 * if (isNote(value)) {
 *   // TypeScript knows value is Note here
 *   console.log(value.noteName);
 * }
 */
export function isNote(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const obj = value;
    return (typeof obj.noteName === 'string' &&
        ['C', 'D', 'E', 'F', 'G', 'A', 'B'].includes(obj.noteName) &&
        typeof obj.modifier === 'string' &&
        ['n', 's', 'f'].includes(obj.modifier));
}
/**
 * Type guard to check if a value is a valid KeySignature object.
 *
 * Validates that:
 * - Value is a non-null object
 * - Has a valid Note object
 * - Has a valid mode (maj, min)
 *
 * @param value - Value to check
 * @returns true if value is a valid KeySignature, false otherwise
 *
 * @example
 * if (isKeySignature(value)) {
 *   // TypeScript knows value is KeySignature here
 *   console.log(value.mode);
 * }
 */
export function isKeySignature(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const obj = value;
    return (isNote(obj.note) &&
        typeof obj.mode === 'string' &&
        ['maj', 'min'].includes(obj.mode));
}
